// SDM — IN INFINITUM · push vidéo (sections/sdm-push-video.liquid, rendu par snippets/sdm-push-video.liquid).
//
// Quatre rôles, pour chaque section :
// - le lecteur YouTube : rien n'est chargé de YouTube avant le clic sur la couverture — ou, en lecture automatique,
//   avant que la vidéo soit à plus de moitié à l'écran (muette, en boucle, en pause dès qu'elle en sort) ;
// - le fond flouté en vidéo : le fichier du même clip hébergé sur Shopify, muet, calé sur la vidéo YouTube — même
//   image, mêmes pauses. Avant la lecture et pendant une publicité YouTube, la couverture floutée, comme à l'écran ;
// - le verre liquide de la pilule de lecture et du bouton : mêmes réglages que le push concert ;
// - le ton de l'en-tête transparent quand la section passe sous lui : `data-sdm-entete` sur <body>, lu par la règle
//   que le snippet écrit avec les couleurs du nuancier.
//
// Un seul lecteur YouTube par section : le fond n'en est pas un. On ne peut pas lire l'image d'un lecteur YouTube (il
// vit dans une fenêtre isolée), et ses règles interdisent de le flouter ou de poser du contenu devant. Le lecteur se
// pilote par messages, comme le fait l'API iframe de YouTube, sans charger son script : on s'annonce (« listening »),
// il répond par son état et sa position (« infoDelivery »), on lui envoie des commandes (« command »).
//
// Un élément personnalisé : l'éditeur de thème remplace la section sans recharger la page, elle se rebranche seule.

const HAUTEUR_ENTETE = 100; // sections/sdm-header.liquid
const YOUTUBE = 'https://www.youtube-nocookie.com';

// mêmes réglages que le bouton du push concert (sections/sdm-concert.liquid), eux-mêmes ceux de la pilule du header
const VERRE = { bezel: 18, depth: 40, chroma: 0.18, blur: 0.8, tint: 0.05, rim: 0.7, spec: 0.28 };

// états du lecteur YouTube
const FIN = 0;
const LECTURE = 1;
const PAUSE = 2;

// Calage du fond : au-delà de 0,8 s d'écart, il saute à l'instant de la vidéo ; en deçà, il accélère ou ralentit un
// peu (10 % au plus, invisible sous le flou) jusqu'à la rattraper. Il n'apparaît qu'à moins de 0,15 s.
const ECART_SAUT = 0.8;
const ECART_VISIBLE = 0.15;
// la position de la vidéo YouTube n'avance plus depuis ce délai : publicité, chargement — le fond attend
const SANS_AVANCE = 700;

let sequence = 0;

/** Fond calculé sans opacité : l'en-tête est transparent (accueil, collections, fiche), le contenu passe dessous. */
const estTransparent = (couleur) => couleur === 'transparent' || /[,/]\s*0(\.0*)?\s*\)$/.test(couleur);

/** Commande au lecteur YouTube ; ignorée tant qu'il n'est pas prêt. */
const commande = (iframe, func, args = []) =>
  iframe?.contentWindow?.postMessage(
    JSON.stringify({ event: 'command', func, args, id: iframe.dataset.yt, channel: 'widget' }),
    YOUTUBE,
  );

class SdmPushVideo extends HTMLElement {
  #nettoyage = [];
  /** @type {HTMLIFrameElement | null} lecteur YouTube */
  #lecteurYT = null;
  /** @type {HTMLVideoElement | null} copie du clip, pour le fond */
  #fond = null;
  #id = '';
  #etat = -1; // état du lecteur YouTube
  #temps = 0; // position du clip dans le lecteur YouTube…
  #tempsLe = 0; // … reçue à cet instant (performance.now)
  #avanceLe = -Infinity; // dernier moment où cette position a avancé
  #vitesse = 1;
  #pub = false; // le lecteur montre une autre vidéo que le clip : une publicité
  #vu = false; // le clip a été montré : en pause, le fond garde son image
  #visible = false;

  connectedCallback() {
    this.#verre();
    this.#entete();
    this.#lecteur();
    this.#fondVideo();
    this.#hauteur();
  }

  disconnectedCallback() {
    this.#nettoyage.forEach((f) => f());
    this.#nettoyage = [];
    this.#fond?.pause();
    if (document.body.dataset.sdmEntete === this.dataset.section) delete document.body.dataset.sdmEntete;
  }

  async #verre() {
    const elements = this.querySelectorAll('[data-refract]');
    const defs = this.querySelector('[data-sdm-video-verre]');
    if (!elements.length || !defs) return;
    try {
      // carte d'import de layout/theme.liquid (snippets/sdm-importmap.liquid)
      const { createGlass, GLASS_DEFAULTS } = await import('sdm/glass');
      if (!this.isConnected) return;
      const glass = createGlass({ defs, params: { ...structuredClone(GLASS_DEFAULTS), ...VERRE } });
      elements.forEach(glass.register);
      glass.applyStyleVars();
    } catch (err) {
      // sans réfraction, le verre garde son liseré et son reflet en CSS
      console.warn('[sdm] verre du push vidéo indisponible', err);
    }
  }

  // Comme le push concert : on observe la bande du haut de l'écran qu'occupe l'en-tête (100 px).
  #entete() {
    const entete = document.querySelector('.sdm-header');
    if (!entete || !('IntersectionObserver' in window)) return;
    const id = this.dataset.section;
    let obs;
    const observer = () => {
      obs?.disconnect();
      obs = new IntersectionObserver(
        ([e]) => {
          const body = document.body;
          // en-tête à fond plein (pages simples) : son ton ne change pas, le contenu ne passe pas dessous
          if (e.isIntersecting && estTransparent(getComputedStyle(entete).backgroundColor)) body.dataset.sdmEntete = id;
          else if (body.dataset.sdmEntete === id) delete body.dataset.sdmEntete;
        },
        { rootMargin: `0px 0px -${Math.max(0, innerHeight - HAUTEUR_ENTETE)}px 0px` },
      );
      obs.observe(this);
    };
    observer();
    addEventListener('resize', observer, { passive: true });
    this.#nettoyage.push(() => {
      obs?.disconnect();
      removeEventListener('resize', observer);
    });
  }

  // Hauteur « comme le push concert » : celle du push concert de la page, mesurée (sur mobile, elle suit son affiche).
  // Sans lui, la feuille de style reprend sa règle. Dans l'éditeur, on remesure quand une section est rechargée.
  #hauteur() {
    if (!this.classList.contains('sdm-video--concert') || !('ResizeObserver' in window)) return;
    const obs = new ResizeObserver(([e]) => {
      // retiré de la page (section supprimée dans l'éditeur) : hauteur nulle
      const h = e.target.isConnected ? (e.borderBoxSize?.[0]?.blockSize ?? e.target.offsetHeight) : 0;
      if (h > 0) this.style.setProperty('--sdm-v-hauteur', `${h}px`);
      else this.style.removeProperty('--sdm-v-hauteur');
    });
    const mesurer = () => {
      obs.disconnect();
      const concert = document.querySelector('.sdm-concert__cadre');
      if (concert) obs.observe(concert);
      else this.style.removeProperty('--sdm-v-hauteur');
    };
    mesurer();
    document.addEventListener('shopify:section:load', mesurer);
    document.addEventListener('shopify:section:unload', mesurer);
    this.#nettoyage.push(() => {
      obs.disconnect();
      document.removeEventListener('shopify:section:load', mesurer);
      document.removeEventListener('shopify:section:unload', mesurer);
    });
  }

  #lecteur() {
    const ecran = this.querySelector('[data-sdm-video-ecran]');
    const id = ecran?.dataset.id;
    if (!id) return;
    this.#id = id;

    // Miniature YouTube : la grande (1280 px) n'existe pas pour toutes les vidéos — YouTube renvoie alors une image
    // grise de 120 px, ou une erreur. Repli sur la moyenne (480 px), qui existe toujours.
    const miniature = ecran.querySelector('[data-sdm-video-miniature]');
    if (miniature) {
      const repli = () => {
        if (miniature.src !== miniature.dataset.repli) miniature.src = miniature.dataset.repli;
      };
      const verifier = () => {
        if (miniature.naturalWidth <= 120) repli();
      };
      if (miniature.complete) verifier();
      miniature.addEventListener('load', verifier);
      miniature.addEventListener('error', repli, { once: true });
    }

    ecran.querySelector('[data-sdm-video-lire]')?.addEventListener('click', () => this.#jouer(ecran, true));

    const auto =
      ecran.dataset.auto === 'true' &&
      !matchMedia('(prefers-reduced-motion: reduce)').matches &&
      !navigator.connection?.saveData &&
      'IntersectionObserver' in window;
    if (!auto) return;
    // YouTube : pas de lecture automatique avant que plus de la moitié du lecteur soit à l'écran
    const obs = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting) {
          if (!this.#lecteurYT) this.#jouer(ecran, false);
          else if (!ecran.dataset.son) commande(this.#lecteurYT, 'playVideo');
        } else if (this.#lecteurYT && !ecran.dataset.son) {
          commande(this.#lecteurYT, 'pauseVideo');
        }
      },
      { threshold: 0.55 },
    );
    obs.observe(ecran);
    this.#nettoyage.push(() => obs.disconnect());
  }

  /**
   * Remplace la couverture par le lecteur YouTube.
   * @param {HTMLElement} ecran
   * @param {boolean} son au clic, avec le son ; en lecture automatique, muette et en boucle
   */
  #jouer(ecran, son) {
    const id = this.#id;
    // un lecteur muet encore en chargement est remplacé par un lecteur avec le son
    this.#lecteurYT?.remove();
    if (son) ecran.dataset.son = 'true';
    if (this.#fond) {
      // le fond se charge pendant que le lecteur YouTube démarre
      this.#fond.preload = 'auto';
      // Au clic, il est lancé une fois (puis arrêté par le calage) : l'iPhone en économie d'énergie refuse la lecture,
      // même muette, qui ne part pas d'un geste.
      if (son) this.#fond.play().catch(() => {});
    }

    const iframe = document.createElement('iframe');
    iframe.dataset.yt = `sdm-yt-${++sequence}`;
    const params = new URLSearchParams({
      autoplay: '1',
      playsinline: '1',
      rel: '0',
      enablejsapi: '1',
      origin: location.origin,
      ...(son ? {} : { mute: '1', loop: '1', playlist: id }),
    });
    iframe.src = `${YOUTUBE}/embed/${encodeURIComponent(id)}?${params}`;
    iframe.title = ecran.dataset.titre || 'Vidéo YouTube';
    iframe.allow = 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share';
    iframe.allowFullscreen = true;
    // YouTube refuse de lire une vidéo intégrée sans l'origine de la page
    iframe.referrerPolicy = 'strict-origin-when-cross-origin';
    ecran.classList.add('sdm-video__ecran--chargement');
    iframe.addEventListener(
      'load',
      () => {
        ecran.classList.remove('sdm-video__ecran--chargement');
        ecran.classList.add('sdm-video__ecran--lecture');
        // le focus quitte la pilule, qui disparaît, pour le lecteur
        if (son) iframe.focus({ preventScroll: true });
        this.#ecouter(iframe);
      },
      { once: true },
    );
    ecran.append(iframe);
    this.#lecteurYT = iframe;
  }

  /** S'abonne à l'état et à la position du lecteur YouTube (annonce répétée jusqu'à sa première réponse, 5 s au plus). */
  #ecouter(iframe) {
    let repondu = false;
    const surMessage = (e) => {
      if (e.origin !== YOUTUBE || e.source !== iframe.contentWindow) return;
      let data;
      try {
        data = JSON.parse(e.data);
      } catch {
        return;
      }
      repondu = true;
      if ((data.event === 'infoDelivery' || data.event === 'initialDelivery') && data.info) this.#suivre(data.info);
    };
    addEventListener('message', surMessage);
    this.#nettoyage.push(() => removeEventListener('message', surMessage));
    let essais = 0;
    const annonce = setInterval(() => {
      if (repondu || ++essais > 20 || !iframe.isConnected) return clearInterval(annonce);
      iframe.contentWindow?.postMessage(
        JSON.stringify({ event: 'listening', id: iframe.dataset.yt, channel: 'widget' }),
        YOUTUBE,
      );
    }, 250);
  }

  /** @param {{ playerState?: number, currentTime?: number, playbackRate?: number, videoData?: { video_id?: string } }} info */
  #suivre(info) {
    const maintenant = performance.now();
    if (typeof info.playerState === 'number') this.#etat = info.playerState;
    if (typeof info.playbackRate === 'number') this.#vitesse = info.playbackRate;
    // une autre vidéo que le clip dans le lecteur : une publicité
    if (info.videoData?.video_id) this.#pub = info.videoData.video_id !== this.#id;
    if (typeof info.currentTime === 'number') {
      if (info.currentTime !== this.#temps) this.#avanceLe = maintenant;
      this.#temps = info.currentTime;
      this.#tempsLe = maintenant;
    }
    this.#caler();
  }

  // Fond flouté en vidéo : le fichier du clip (Shopify). Jamais en mouvement réduit ni en économie de données — la
  // couverture floutée reste.
  #fondVideo() {
    const video = /** @type {HTMLVideoElement | null} */ (this.querySelector('[data-sdm-video-fond-video]'));
    if (!video) return;
    if (matchMedia('(prefers-reduced-motion: reduce)').matches || navigator.connection?.saveData) {
      video.remove();
      return;
    }
    video.muted = true;
    this.#fond = video;
    video.addEventListener('seeked', () => this.#caler());
    video.addEventListener('playing', () => this.#caler());
    if (!('IntersectionObserver' in window)) {
      this.#visible = true;
      return;
    }
    // hors de l'écran, le fond s'arrête ; il se recale en revenant
    const obs = new IntersectionObserver(
      ([e]) => {
        this.#visible = e.isIntersecting;
        this.#caler();
      },
      { rootMargin: '100px 0px' },
    );
    obs.observe(this);
    this.#nettoyage.push(() => obs.disconnect());
  }

  /** Le fond montre la même image que la vidéo YouTube : même instant, mêmes pauses. */
  #caler() {
    const video = this.#fond;
    if (!video) return;
    const conteneur = video.parentElement;
    // le fichier s'arrête avant la vidéo YouTube (montage plus court) : au-delà, la couverture
    const horsFichier = video.duration > 0 && this.#temps > video.duration;
    const clip = !this.#pub && !horsFichier && performance.now() - this.#avanceLe < SANS_AVANCE;

    if (this.#etat !== LECTURE || !clip || !this.#visible) {
      if (!video.paused) video.pause();
      // en pause : l'image exacte où la vidéo s'est arrêtée (en chargement, hors de l'écran : l'image où il en est)
      const fige = this.#etat === PAUSE && this.#vu && !this.#pub;
      if (fige && !video.seeking && Math.abs(video.currentTime - this.#temps) > 0.05) video.currentTime = this.#temps;
      // avant la lecture, à la fin, pendant une publicité : la couverture, comme à l'écran
      if (!this.#vu || this.#pub || horsFichier || this.#etat === FIN) conteneur.classList.remove('sdm-video__fond--lecture');
      return;
    }

    // position actuelle de la vidéo YouTube, extrapolée depuis son dernier message (un tous les quarts de seconde)
    const cible = this.#temps + ((performance.now() - this.#tempsLe) / 1000) * this.#vitesse;
    if (video.seeking) return;
    const ecart = cible - video.currentTime; // > 0 : le fond est en retard
    if (Math.abs(ecart) > ECART_SAUT || video.readyState < 2) {
      video.playbackRate = this.#vitesse;
      // un saut coûte un instant de chargement : on vise un peu plus loin
      video.currentTime = Math.min(cible + 0.1, video.duration || cible + 0.1);
    } else {
      video.playbackRate = this.#vitesse * Math.min(1.1, Math.max(0.9, 1 + ecart * 0.5));
    }
    if (video.paused) video.play().catch(() => {});
    if (Math.abs(ecart) < ECART_VISIBLE && !video.paused) {
      conteneur.classList.add('sdm-video__fond--lecture');
      this.#vu = true;
    }
  }
}

if (!customElements.get('sdm-push-video')) customElements.define('sdm-push-video', SdmPushVideo);